import React from 'react';
import { Box, Checkbox, FormControlLabel, FormHelperText } from '@mui/material';
import { Control, Controller, FieldErrors, UseFormSetValue, UseFormWatch } from 'react-hook-form';

import { DatePicker } from '../../Paylinks/components';
import {
  BaseFilterSection as Section,
  filterSelectAllRowSx,
} from '../../Paylinks/components/BaseFiltersPanel';
import { auditLogResource, AuditLogResource } from '../../../store/modules/merchant/types';
import { AuditLogFiltersFormValues } from '../../../hooks/useAuditLogFiltersForm';
import { getAuditLogResourceLabel } from '../resourceLabels';

interface Props {
  hookFormProps: {
    watch: UseFormWatch<AuditLogFiltersFormValues>;
    setValue: UseFormSetValue<AuditLogFiltersFormValues>;
    control: Control<AuditLogFiltersFormValues>;
    errors: FieldErrors<AuditLogFiltersFormValues>;
  };
}

export const AuditLogFilters = ({ hookFormProps }: Props) => {
  const { watch, setValue, control, errors } = hookFormProps;
  const selectedResources = watch('resources');
  const createdAfter = watch('createdAfter');
  const createdBefore = watch('createdBefore');

  return (
    <>
      <Section
        title="Resources"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          '& > *': {
            height: '24px',
            width: 'fit-content',
          },
          '& span': {
            textTransform: 'capitalize',
            fontSize: 14,
          },
        }}
      >
        <FormControlLabel
          sx={filterSelectAllRowSx}
          control={
            <Checkbox
              indeterminate={
                selectedResources.length > 0 && selectedResources.length < auditLogResource.length
              }
              checked={selectedResources.length === auditLogResource.length}
              onChange={(e) =>
                e.target.checked
                  ? setValue('resources', auditLogResource as unknown as AuditLogResource[])
                  : setValue('resources', [])
              }
            />
          }
          label="Select All"
        />
        {auditLogResource.map((resource) => (
          <FormControlLabel
            key={resource}
            control={
              <Checkbox
                checked={selectedResources.includes(resource)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setValue('resources', [...selectedResources, resource]);
                  } else {
                    setValue(
                      'resources',
                      selectedResources.filter((selected) => selected !== resource),
                    );
                  }
                }}
              />
            }
            label={getAuditLogResourceLabel(resource)}
          />
        ))}
        {errors.resources && (
          <FormHelperText error sx={{ fontSize: 10, mt: 0.5 }}>
            {errors.resources.message}
          </FormHelperText>
        )}
      </Section>
      <Section title="Date Range">
        <Box sx={{ display: 'flex', gap: 1 }}>
          {(['createdAfter', 'createdBefore'] as const).map((name) => (
            <Controller
              key={name}
              name={name}
              control={control}
              render={({ field }) => (
                <DatePicker
                  key={name}
                  label={name === 'createdAfter' ? 'Start date' : 'End date'}
                  errorMessage={errors[name]?.message}
                  disablePast={false}
                  dateOnly
                  shouldDisableDate={(date) => {
                    if (name === 'createdAfter' && createdBefore) {
                      return date.isAfter(createdBefore);
                    }

                    if (name === 'createdBefore' && createdAfter) {
                      return date.isBefore(createdAfter);
                    }

                    return false;
                  }}
                  closeOnSelect
                  {...field}
                />
              )}
            />
          ))}
        </Box>
      </Section>
    </>
  );
};
